/**
 * Adaptação e formatação puras do **detalhe de notas e frequência** do aluno
 * (`StudentGradeDetailViewModel`, endpoint `grade-detail`) para o modelo de exibição do
 * card "Notas e frequência por disciplina".
 *
 * Achata os módulos → disciplinas em uma lista plana de disciplinas (o card não exibe o
 * agrupamento por módulo, apenas usa o índice do módulo para colorir o marcador da
 * disciplina). Todas as funções são puras e não dependem de React/i18n.
 */

import type { StudentGradeDetailViewModel } from '@/shared/api/generated';

/** Nota mínima de aprovação (escala 0–10). */
export const PASS_GRADE = 7;
/** Frequência mínima de suficiência (%). */
export const PASS_FREQUENCY = 75;

/** Tom semântico de um valor de nota/frequência. */
export type GradeTone = 'ok' | 'bad' | 'neutral';

/** Linha de aula (prova) dentro de uma disciplina. */
export interface GradeDetailClassRow {
  /** Chave estável para a lista do React. */
  readonly key: string;
  /** Nome da aula (ClassroomDiscipline). */
  readonly lessonName: string;
  /** Título da prova principal da aula, ou `''` quando ausente. */
  readonly examName: string;
  /** Nota do aluno (0–10), ou `null` quando ainda não lançada. */
  readonly grade: number | null;
  /** Peso da prova no cálculo da média, ou `null` quando ausente. */
  readonly weight: number | null;
}

/** Bucket de peso da fórmula: aulas agrupadas por peso de prova. */
export interface GradeFormulaBucket {
  /** Símbolo do fator na fórmula (`A1`, `A2`, …), ligado ao chip da legenda. */
  readonly symbol: string;
  /** Peso da prova que define o bucket. */
  readonly weight: number;
  /** Média das notas das aulas com este peso. */
  readonly grade: number;
  /** Contribuição do bucket na soma (`grade × weight`). */
  readonly product: number;
  /** Quantidade de aulas no bucket. */
  readonly count: number;
}

/**
 * Decomposição do cálculo da média ponderada da disciplina, agrupada por peso de prova
 * (espelha o backend: cada peso é um "tipo de avaliação"; a média da disciplina é a média
 * ponderada das médias por peso, dividida pela soma dos pesos distintos).
 */
export interface GradeFormula {
  readonly buckets: readonly GradeFormulaBucket[];
  /** Soma das contribuições (`Σ grade × weight`). */
  readonly sumProducts: number;
  /** Soma dos pesos distintos (divisor). */
  readonly sumWeights: number;
  /** Resultado (`sumProducts / sumWeights`). */
  readonly result: number;
}

/** Disciplina achatada, com suas aulas e o índice do módulo (para a cor do marcador). */
export interface GradeDetailDisciplineRow {
  readonly key: string;
  /** Índice do módulo de origem (cor cíclica); `-1` quando sem módulo. */
  readonly moduleIndex: number;
  readonly name: string;
  /** Média ponderada da disciplina (0–10), ou `null`. */
  readonly grade: number | null;
  /** Frequência da disciplina (%), ou `null`. */
  readonly frequency: number | null;
  readonly classes: readonly GradeDetailClassRow[];
  /** Decomposição do cálculo da média, ou `null` quando não há aulas com nota e peso. */
  readonly formula: GradeFormula | null;
}

/**
 * Reconstrói a decomposição da média ponderada de uma disciplina agrupando as aulas por
 * peso de prova (média das notas em cada peso × peso ÷ soma dos pesos distintos).
 *
 * Considera apenas aulas com nota e peso (> 0) preenchidos; retorna `null` quando não há
 * nenhuma aula computável.
 *
 * @param classes - Aulas da disciplina.
 */
export function buildDisciplineGradeFormula(
  classes: readonly GradeDetailClassRow[],
): GradeFormula | null {
  const buckets = new Map<number, { sum: number; count: number }>();
  for (const cls of classes) {
    if (cls.grade === null || cls.weight === null || cls.weight <= 0) continue;
    const acc = buckets.get(cls.weight) ?? { sum: 0, count: 0 };
    acc.sum += cls.grade;
    acc.count += 1;
    buckets.set(cls.weight, acc);
  }
  if (buckets.size === 0) return null;

  const rows: GradeFormulaBucket[] = [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([weight, { sum, count }], index) => {
      const grade = sum / count;
      return { symbol: `A${index + 1}`, weight, grade, product: grade * weight, count };
    });

  const sumProducts = rows.reduce((total, b) => total + b.product, 0);
  const sumWeights = rows.reduce((total, b) => total + b.weight, 0);
  return {
    buckets: rows,
    sumProducts,
    sumWeights,
    result: sumWeights > 0 ? sumProducts / sumWeights : 0,
  };
}

/**
 * Classifica um valor conforme o mínimo: `ok` (≥ mínimo), `bad` (< mínimo) ou `neutral`
 * (valor ausente).
 *
 * @param value - Valor a classificar (nota ou frequência).
 * @param min - Mínimo de aprovação/suficiência.
 */
export function gradeTone(value: number | null | undefined, min: number): GradeTone {
  if (value === null || value === undefined) return 'neutral';
  return value >= min ? 'ok' : 'bad';
}

/**
 * Formata uma nota (0–10) com casas decimais fixas; `null`/`undefined` → `"—"`.
 *
 * @param value - Nota a formatar.
 * @param decimals - Casas decimais (default `1`).
 */
export function fmtGrade(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return '—';
  return value.toFixed(decimals);
}

/**
 * Formata uma frequência como percentual com uma casa decimal (ex.: `79.4%`);
 * `null`/`undefined` → `"—"`.
 *
 * @param value - Frequência (0–100).
 */
export function fmtFrequency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${(Math.round(value * 10) / 10).toFixed(1)}%`;
}

/**
 * Formata o peso da prova (inteiro quando possível); `null`/`undefined` → `"—"`.
 *
 * @param value - Peso da prova.
 */
export function fmtWeight(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(Math.round(value * 100) / 100);
}

/**
 * Achata os módulos do detalhe do aluno em uma lista plana de disciplinas, preservando o
 * índice do módulo de origem (para a cor do marcador) e mapeando cada aula em uma linha.
 *
 * @param detail - Detalhe de notas e frequência do aluno; `undefined` → `[]`.
 * @returns Disciplinas achatadas na ordem em que aparecem nos módulos.
 */
export function buildGradeDetailDisciplines(
  detail: StudentGradeDetailViewModel | undefined,
): readonly GradeDetailDisciplineRow[] {
  const rows: GradeDetailDisciplineRow[] = [];
  (detail?.modules ?? []).forEach((module, moduleIndex) => {
    (module.disciplines ?? []).forEach((discipline, disciplineIndex) => {
      const disciplineKey =
        discipline.disciplineId != null
          ? `m${module.id ?? moduleIndex}_d${discipline.disciplineId}`
          : `m${moduleIndex}_d${disciplineIndex}`;
      const classes: GradeDetailClassRow[] = (discipline.classes ?? []).map((cls, classIndex) => ({
        key:
          cls.classroomDisciplineId != null
            ? `${disciplineKey}_c${cls.classroomDisciplineId}`
            : `${disciplineKey}_c${classIndex}`,
        lessonName: cls.classroomDisciplineName ?? '',
        examName: cls.examName ?? '',
        grade: cls.grade ?? null,
        weight: cls.weight ?? null,
      }));
      rows.push({
        key: disciplineKey,
        moduleIndex,
        name: discipline.disciplineName ?? '',
        grade: discipline.grade ?? null,
        frequency: discipline.frequency ?? null,
        classes,
        formula: buildDisciplineGradeFormula(classes),
      });
    });
  });
  return rows;
}
